import { useEffect, useState } from 'react';

import type { CreativeTemplateDto } from '@/services/ws-appnexus-proxy/src/main/resources/rest-api';
import type {
  AccountShort,
  AcTemplateDto,
  CampaignPositionDto,
  CreativeDto,
  CreativeForm as CreativeFormDto,
  ImageDto,
  ThirdPartyPixelDto,
  ThirdPartyPixelFormat as ThirdPartyPixelFormatType,
} from '@/services/ws-creatives/src/main/resources/rest-api';
import { CreativeType, ImageType, LanguageType, ThirdPartyPixelFormat } from '@/services/ws-creatives/src/main/resources/rest-api';
import { fromDateTimeType } from '@/js/libs/api-helpers';

import { PLACEHOLDER_TAG_CONTENT } from './constants.ts';
import type { FormSubmitValues, FormValues } from './schema.ts';

const CONTENT_PIXEL_FORMATS: ThirdPartyPixelFormatType[] = [ThirdPartyPixelFormat.RAW_JS, ThirdPartyPixelFormat.RAW_URL];

export const isContentPixelFormat = (format: ThirdPartyPixelFormatType) => CONTENT_PIXEL_FORMATS.includes(format);

const buildInitialMacroses = (
  savedMacroses: Record<string, string> | undefined,
  acTemplate?: AcTemplateDto,
  creativeTemplate?: CreativeTemplateDto,
): Record<string, string> | null => {
  const templateMacros = acTemplate?.macros ?? creativeTemplate?.macroses ?? [];
  if (templateMacros.length === 0 && !savedMacroses) {
    return null;
  }
  const result: Record<string, string> = {};
  templateMacros.forEach((macro) => {
    result[macro.code] = macro.defaultValue ?? '';
  });
  if (savedMacroses) {
    Object.entries(savedMacroses).forEach(([key, value]) => {
      result[key] = value ?? '';
    });
  }
  return result;
};

export const formatPositionLabel = (position: CampaignPositionDto): string =>
  [
    `${position.campaignInternalOrderNumber} -`,
    `${position.sortOrder}:`,
    position.name,
    `(${fromDateTimeType(position.startDate).format('DD.MM.YY')} - ${fromDateTimeType(position.endDate).format('DD.MM.YY')})`,
  ].join(' ');

// Editing a creative that runs in several campaigns changes it in all of them at once, which is what
// the user has to be warned about — a creative linked within a single campaign carries no such surprise.
export const isCreativeLinkedToMultipleCampaigns = (creative?: CreativeDto): boolean =>
  new Set((creative?.campaignPositions ?? []).map((position) => position.campaignId)).size > 1;

export const buildEmptyDefaultValues = (advertiserAccount?: AccountShort): FormValues => ({
  account: advertiserAccount ? { label: advertiserAccount.displayName ?? advertiserAccount.name, value: advertiserAccount.id } : null,
  creativeType: {
    value: CreativeType.NATIVE,
    label: CreativeType.NATIVE,
  },
  name: '',
  languageType: LanguageType.DE,
  viewpixels: [],
  title: '',
  description: '',
  link: '',
  linkText: '',
  alternativeTitle: '',
  width: null,
  height: null,
  shouldSaveSize: false,
  content: '',
  acTemplate: null,
  adServerType: null,
  creativeTemplate: null,
  macroses: null,
  macroFileNames: {},
  positions: [],
  thirdPartyPixels: [],
  images: [],
  htmlAsset: null,
  mediaAsset: null,
});

const slotImageTypesByCreativeType: Partial<Record<CreativeType, ImageType[]>> = {
  [CreativeType.NATIVE]: [ImageType.NATIVE1X1, ImageType.NATIVE16X9],
  [CreativeType.BANNER]: [ImageType.BANNER],
  [CreativeType.NEWSLETTER_DISPLAY]: [ImageType.BANNER],
  [CreativeType.NEWSLETTER_NATIVE]: [ImageType.NEWSLETTER_NATIVE],
};

// The creative can hold more images than its type needs (e.g. leftovers from historical data),
// while the backend requires the exact image set on save — map only the relevant ones to the form slots.
const mapImagesToFormSlots = (creative: CreativeDto): ImageDto[] => {
  const slotImageTypes = slotImageTypesByCreativeType[creative.creativeType] ?? [];
  const images = creative.images ?? [];
  return slotImageTypes.flatMap((slotImageType) => {
    const image = images.find((candidate) => candidate.imageType === slotImageType);
    return image ? [image] : [];
  });
};

export const mapApiToForm = (creative: CreativeDto, acTemplate?: AcTemplateDto, creativeTemplate?: CreativeTemplateDto): FormValues => ({
  account: {
    label: creative.advertiserName ?? creative.accountId.toString(),
    value: creative.accountId,
  },
  creativeType: {
    label: creative.creativeType,
    value: creative.creativeType,
  },
  name: creative.name,
  languageType: creative.languageType ?? LanguageType.DE,
  viewpixels: (creative.viewpixels ?? []).map((value) => ({ value })),
  title: creative.title ?? '',
  description: creative.description ?? '',
  link: creative.link ?? '',
  linkText: creative.linkText ?? '',
  alternativeTitle: creative.alternativeTitle ?? '',
  width: creative.width ?? null,
  height: creative.height ?? null,
  shouldSaveSize: false,
  content: creative.content === PLACEHOLDER_TAG_CONTENT ? '' : (creative.content ?? ''),
  acTemplate: acTemplate ? { label: acTemplate.name, value: acTemplate } : null,
  adServerType: creative.adServerType ? { label: creative.adServerType, value: creative.adServerType } : null,
  creativeTemplate: creativeTemplate?.name ? { label: creativeTemplate.name, value: creativeTemplate } : null,
  macroses: buildInitialMacroses(creative.macroses, acTemplate, creativeTemplate),
  macroFileNames: creative.macroFileNames ?? {},
  positions: creative.campaignPositions?.map((position) => ({ label: formatPositionLabel(position), value: position })) ?? [],
  thirdPartyPixels: (creative.thirdPartyPixels ?? []).map((pixel) => ({
    format: { label: pixel.format, value: pixel.format },
    value: (isContentPixelFormat(pixel.format) ? pixel.content : pixel.url) ?? '',
  })),
  images: mapImagesToFormSlots(creative),
  htmlAsset: creative.htmlAsset ?? null,
  mediaAsset: creative.mediaAsset ?? null,
});

export const mapFormToApi = (values: FormSubmitValues): CreativeFormDto => {
  if (!values.account) {
    throw new Error('mapFormToApi called with invalid form values');
  }

  const creativeType = values.creativeType.value;

  const thirdPartyPixels = values.thirdPartyPixels.reduce<ThirdPartyPixelDto[]>((acc, pixel) => {
    const value = pixel.value.trim();
    if (!value) {
      return acc;
    }
    const format = pixel.format.value;
    if (isContentPixelFormat(format)) {
      return [...acc, { format, content: value }];
    }
    return [...acc, { format, url: value }];
  }, []);

  return {
    name: values.name,
    accountId: values.account.value,
    creativeType,
    languageType: values.languageType,
    title: values.title || undefined,
    description: values.description || undefined,
    link: values.link || undefined,
    linkText: values.linkText || undefined,
    alternativeTitle: values.alternativeTitle || undefined,
    width: values.width ?? undefined,
    height: values.height ?? undefined,
    content: creativeType === CreativeType.HTML_TAG ? (values.content.trim() ? values.content : PLACEHOLDER_TAG_CONTENT) : undefined,
    acTemplateId: creativeType === CreativeType.AC ? values.acTemplate?.value.id : undefined,
    adServerType: creativeType === CreativeType.AC ? values.adServerType?.value : undefined,
    templateId:
      creativeType === CreativeType.HTML_TAG || creativeType === CreativeType.BANNER ? values.creativeTemplate?.value.id : undefined,
    macroses: values.macroses ?? undefined,
    macroFileNames: values.macroFileNames,
    htmlAssetId: values.htmlAsset?.id ?? undefined,
    mediaAssetId: values.mediaAsset?.id ?? undefined,
    campaignPositionIds: values.positions.map((option) => option.value.id),
    thirdPartyPixels,
    imageIds: values.images.map((image) => image.id),
    viewpixels: values.viewpixels.map(({ value }) => value.trim()).filter(Boolean),
  };
};

export const readImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions'));
    };
    img.src = url;
  });

export const useImageDimensionsFromUrl = (url: string | undefined) => {
  const [size, setSize] = useState<{ width: number; height: number }>();

  useEffect(() => {
    if (!url) {
      return;
    }
    const img = new Image();
    img.onload = () => setSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = url;
  }, [url]);

  return url ? size : undefined;
};
